import { Link } from "react-router-dom";
import { Linkedin, Instagram, Facebook } from "lucide-react";
import SiftWordmark from "./SiftWordmark";
import { isNative } from "../lib/platform";

const SOCIALS = [
  { href: "https://www.linkedin.com/company/siftcareers", label: "LinkedIn", Icon: Linkedin },
  { href: "https://www.instagram.com/siftcareers", label: "Instagram", Icon: Instagram },
  { href: "https://www.facebook.com/siftcareers", label: "Facebook", Icon: Facebook },
];

const colHeader = {
  fontFamily: "var(--font-mono)",
  fontSize: "0.6875rem",
  letterSpacing: ".16em",
  textTransform: "uppercase",
  color: "var(--text-tertiary)",
  marginBottom: 14,
  fontWeight: 500,
};

export default function SiteFooter() {
  const link = "footer-link";
  return (
    <footer style={{ background: "var(--surface-sunken)", borderTop: "1px solid var(--border)", color: "var(--text-secondary)", fontSize: "0.875rem" }}>
      <style>{`
        .${link}{ color:var(--text-secondary); transition:color .2s; }
        .${link}:hover{ color:var(--brand); }
      `}</style>
      <div style={{ maxWidth: 1480, margin: "0 auto" }} className="px-6 lg:px-12 py-14">
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "40px 48px" }}>
          {/* Brand blurb */}
          <div style={{ maxWidth: 300 }}>
            <SiftWordmark size={30} />
            <p style={{ color: "var(--text-tertiary)", lineHeight: 1.6, maxWidth: 280, marginTop: 16 }}>
              A premium services marketplace and workforce platform. Book trusted pros, get hired
              directly, and run the work in one place, with one transparent fee.
            </p>
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              {SOCIALS.map(({ href, label, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="no-underline"
                  style={{
                    width: 38, height: 38, borderRadius: "var(--r-pill)",
                    border: "1px solid var(--border-strong)", background: "var(--surface)",
                    display: "grid", placeItems: "center", color: "var(--text-secondary)",
                  }}
                >
                  <Icon size={17} strokeWidth={1.6} aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>

          {/* Link cluster (kept to the right so the negative space reads intentional) */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "40px 64px" }}>
          {/* Get started — reinforces the guiding principle: create an account */}
          <div>
            <h4 style={colHeader}>Get started</h4>
            <ul className="space-y-2.5">
              <li><Link to="/get-started?a=business&c=1" className={link}>I'm hiring</Link></li>
              <li><Link to="/auth/register" className={link}>I'm looking for work</Link></li>
              <li><Link to="/auth/login" className={link}>Sign in</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 style={colHeader}>Company</h4>
            <ul className="space-y-2.5">
              <li><Link to="/about" className={link}>About</Link></li>
              <li><Link to="/contact" className={link}>Contact</Link></li>
              <li><Link to="/blog" className={link}>Blog</Link></li>
              <li><Link to="/partner" className={link}>Partner Program</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 style={colHeader}>Legal</h4>
            <ul className="space-y-2.5">
              <li><Link to="/privacy" className={link}>Privacy Policy</Link></li>
              <li><Link to="/security" className={link}>Security</Link></li>
              <li><Link to="/terms" className={link}>Terms of Service</Link></li>
              <li><Link to="/accessibility" className={link}>Accessibility</Link></li>
              {/* Web only — the native apps don't track, so there's no cookie
                  preference to manage (keeps the app free of tracking prompts
                  per Apple 5.1.2). */}
              {!isNative() && (
                <li>
                  <button
                    type="button"
                    onClick={() => window.dispatchEvent(new CustomEvent("sift:open-cookie-preferences"))}
                    className={`${link} no-underline`}
                    style={{ background: "none", border: "none", padding: 0, font: "inherit", textAlign: "left", cursor: "pointer" }}
                  >
                    Cookie preferences
                  </button>
                </li>
              )}
            </ul>
          </div>
          </div>
        </div>

        <div
          style={{ marginTop: 48, paddingTop: 24, borderTop: "1px solid var(--border)", color: "var(--text-muted)" }}
          className="flex flex-col sm:flex-row justify-between items-center gap-4"
        >
          <p style={{ margin: 0 }}>&copy; {new Date().getFullYear()} Sift Careers, LLC. All rights reserved. Patent Pending.</p>
          <div className="flex gap-6">
            <a href="mailto:support@siftcareers.com" className={link}>support@siftcareers.com</a>
            <span>Carmel, IN 46032</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
